import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Hotel as HotelIcon, MapPin, Phone, BedDouble, Star, Clock,
  Image as ImageIcon,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, patch, upload } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import {
  Card, Button, Modal, Field, Input, Badge, Loader, EmptyState,
  SearchInput, SectionTitle, stagger, fadeUp,
} from '../../components/UI';
import { fmtNumber } from '../../utils/format';

const BLANK = {
  name: '', line1: '', city: '', state: '', pincode: '',
  contactEmail: '', contactMobile: '',
};

export default function Hotels() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [logo, setLogo] = useState(null);            // the File awaiting upload
  const [logoPreview, setLogoPreview] = useState(null);
  const logoRef = useRef(null);

  const { data: rows, loading, reload } = useFetch('/hotels', { limit: 60, ...(search ? { search } : {}) });

  const openNew = () => {
    setEditing(null); setForm(BLANK); setLogo(null); setLogoPreview(null);
    setCats([{ name: 'Deluxe', occupancy: 2, totalRooms: 0, tariff: 0 }]);
    setOpen(true);
  };
  const openEdit = (h) => {
    setEditing(h._id);
    setForm({
      name: h.name || '',
      line1: h.address?.line1 || '', city: h.address?.city || '',
      state: h.address?.state || '', pincode: h.address?.pincode || '',
      contactEmail: h.contactEmail || '', contactMobile: h.contactMobile || '',
    });
    setLogo(null);
    setLogoPreview(h.logo || null);
    setCats(h.roomCategories?.map((c) => ({ name: c.name, occupancy: c.occupancy, totalRooms: c.totalRooms, tariff: c.tariff })) || []);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Hotel name is required');
    setSaving(true);
    // Only what this form collects is sent — room categories, star rating and
    // check-in times stay untouched on a hotel that already has them.
    const payload = {
      name: form.name.trim(),
      address: {
        line1: form.line1,
        city: form.city,
        state: form.state,
        pincode: form.pincode || undefined,
      },
      contactEmail: form.contactEmail,
      contactMobile: form.contactMobile,
      ...(editing ? {} : {
        roomCategories: cats.filter((c) => c.name?.trim()).map((c) => ({
          name: c.name.trim(),
          occupancy: Number(c.occupancy) || 2,
          totalRooms: Number(c.totalRooms) || 0,
          tariff: Number(c.tariff) || 0,
        })),
      }),
    };
    try {
      const res = editing
        ? await patch(`/hotels/${editing}`, payload)
        : await post('/hotels', payload);

      // The logo endpoint is keyed on the hotel, so it goes up once it exists.
      const hotelId = editing || res.data?._id;
      if (logo && hotelId) await upload(`/hotels/${hotelId}/logo`, logo);

      toast.success(editing ? 'Hotel updated' : 'Hotel added');
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <>
      <SectionTitle
        title="Partner hotels"
        subtitle="Properties that host your event attendees"
        action={<Button icon={Plus} onClick={openNew}>Add hotel</Button>}
      />

      <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search hotels…" style={{ maxWidth: 380, marginBottom: 18 }} />

      {loading ? <Loader label="Loading hotels" /> : !rows?.length ? (
        <EmptyState
          message={search ? 'No hotels match your search' : 'No hotels added yet'}
          hint="Add a property and its room categories so rooms can be allocated to attendees."
          icon={HotelIcon}
          action={!search && <Button icon={Plus} onClick={openNew}>Add hotel</Button>}
        />
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15 }}>
          {rows.map((h) => (
            <motion.div key={h._id} variants={fadeUp}>
              <Card hover onClick={() => openEdit(h)} style={{ cursor: 'pointer', height: '100%' }}>
                <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 'var(--r)', flexShrink: 0,
                    background: 'rgba(239,212,138,0.1)', display: 'grid', placeItems: 'center',
                  }}>
                    <HotelIcon size={20} color="var(--gold-500)" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>{h.name}</h3>
                    {h.starRating && (
                      <div style={{ display: 'flex', gap: 2, marginTop: 5 }}>
                        {Array.from({ length: Math.min(h.starRating, 5) }).map((_, i) => (
                          <Star key={i} size={11} fill="var(--gold-400)" color="var(--gold-400)" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.3, color: 'var(--text-mid)' }}>
                  {h.address?.city && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MapPin size={13} color="var(--gold-600)" /> {h.address.city}{h.address.state ? `, ${h.address.state}` : ''}
                    </span>
                  )}
                  {h.contactMobile && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Phone size={13} color="var(--gold-600)" /> {h.contactMobile}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={13} color="var(--gold-600)" /> In {h.checkInTime} · Out {h.checkOutTime}
                  </span>
                </div>

                <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid var(--ink-750)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 10 }}>
                    <BedDouble size={14} color="var(--gold-500)" />
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold-400)' }}>{fmtNumber(h.totalRooms)}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>rooms available</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {h.roomCategories?.slice(0, 3).map((c) => (
                      <Badge key={c._id || c.name} tone="neutral">{c.name} · {c.totalRooms}</Badge>
                    ))}
                    {h.roomCategories?.length > 3 && <Badge tone="neutral">+{h.roomCategories.length - 3}</Badge>}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      <Modal
        open={open} onClose={() => setOpen(false)} width={640}
        title={editing ? 'Edit hotel' : 'Add a hotel'}
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} loading={saving}>{editing ? 'Save changes' : 'Add hotel'}</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Photo" hint="Optional — shown to guests on their stay card">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) { setLogo(f); setLogoPreview(URL.createObjectURL(f)); }
              }}
              onClick={() => logoRef.current?.click()}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
                padding: 12, borderRadius: 'var(--r)',
                border: `1.5px dashed ${logoPreview ? 'var(--gold-500)' : 'var(--border)'}`,
                background: 'var(--ink-900)',
              }}
            >
              <input
                ref={logoRef} type="file" hidden accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setLogo(f); setLogoPreview(URL.createObjectURL(f)); }
                }}
              />
              {logoPreview ? (
                <img
                  src={logoPreview} alt=""
                  style={{ width: 58, height: 58, objectFit: 'cover', borderRadius: 'var(--r-sm)', flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 58, height: 58, borderRadius: 'var(--r-sm)', flexShrink: 0,
                  background: 'var(--ink-800)', display: 'grid', placeItems: 'center',
                }}>
                  <ImageIcon size={20} color="var(--text-lo)" />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13 }}>{logo ? logo.name : logoPreview ? 'Current photo' : 'Drop an image, or click to browse'}</p>
                <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 3 }}>JPG, PNG or WebP</p>
              </div>
            </div>
          </Field>

          <Field label="Hotel name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="The Leela Palace" />
          </Field>

          <Field label="Address">
            <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Diplomatic Enclave, Chanakyapuri" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 13 }}>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="New Delhi" />
            </Field>
            <Field label="State">
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Delhi" />
            </Field>
            <Field label="Pincode" hint="Optional">
              <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="110023" />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Front desk email">
              <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="frontdesk@hotel.com" />
            </Field>
            <Field label="Front desk number">
              <Input value={form.contactMobile} onChange={(e) => setForm({ ...form, contactMobile: e.target.value })} placeholder="011 2611 0000" />
            </Field>
          </div>
        </div>
      </Modal>
    </>
  );
}
